import {
  Card,
  Col,
  DatePicker,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { Dayjs } from "dayjs";
import {
  getCompanies,
  getDashboardIncidents,
  getDashboardRisks,
  getDashboardQuestionnaireStatus,
  getTrainings,
} from "../repository/data.repository";
import {
  DashboardIncidents,
  DashboardQuestionnaireStatus,
  DashboardRisks,
  Training,
} from "../repository/data.default";
import { createSearchRequest } from "../hooks/useSearchRequest";
import RemoteSelect from "../components/analytics/RemoteSelect";
import { QueryOperation } from "../code/search-query/search-query";

// PD-01 §10 — consolidated per-company report. One company's: assessment
// completion, risk summary, incidents, trainings. The idea is a sign-off
// snapshot — not a drilldown. Everything comes from Dashboard endpoints
// filtered to the picked company (and optional date range).
function CompanyReportsPage() {
  const [companyUid, setCompanyUid] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);

  const [risks, setRisks] = useState<DashboardRisks | undefined>();
  const [incidents, setIncidents] = useState<DashboardIncidents | undefined>();
  const [status, setStatus] =
    useState<DashboardQuestionnaireStatus | undefined>();
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [loading, setLoading] = useState(false);

  const companiesRequest = createSearchRequest(getCompanies, "name");

  const from = range?.[0]?.startOf("day").toISOString();
  const to = range?.[1]?.endOf("day").toISOString();

  useEffect(() => {
    if (!companyUid) {
      setRisks(undefined);
      setIncidents(undefined);
      setStatus(undefined);
      setTrainings([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const scope = { companyUid, from, to };
    const trainingsConditions: any[] = [
      { field: "companyUid", values: [companyUid], operation: QueryOperation.Equal },
    ];
    if (from && to) {
      trainingsConditions.push({
        field: "startDate",
        values: [from, to],
        operation: QueryOperation.Between,
      });
    }
    Promise.all([
      getDashboardRisks(scope),
      getDashboardIncidents(scope),
      getDashboardQuestionnaireStatus(scope),
      getTrainings({
        searchQuery: { conditions: trainingsConditions },
        pageNumber: 0,
        pageSize: 200,
      }),
    ])
      .then(([r, i, s, t]) => {
        if (cancelled) return;
        setRisks(r);
        setIncidents(i);
        setStatus(s);
        setTrainings(t.data);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [companyUid, from, to]);

  const trainingTotals = useMemo(
    () => ({
      total: trainings.length,
      completed: trainings.filter((t) => t.status === "Completed").length,
      cancelled: trainings.filter((t) => t.status === "Cancelled").length,
      withFeedback: trainings.filter((t) => (t.feedback ?? "").trim().length > 0)
        .length,
    }),
    [trainings],
  );

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Company Reports
        </Typography.Title>
      </Row>
      <Col className="flex flex-auto flex-col gap-4 overflow-hidden p-4 pt-0">
        <Row align="middle" gutter={12}>
          <Col>
            <Typography.Text type="secondary" className="mr-2">
              Company
            </Typography.Text>
            <RemoteSelect
              className="w-80"
              placeholder="Pick a company"
              value={companyUid}
              onChange={setCompanyUid}
              request={companiesRequest}
            />
          </Col>
          <Col>
            <Typography.Text type="secondary" className="mr-2">
              Period
            </Typography.Text>
            <DatePicker.RangePicker
              value={range}
              onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
              allowClear
            />
          </Col>
        </Row>

        {!companyUid ? (
          <Card>
            <Typography.Text type="secondary">
              Pick a company above to see its consolidated report.
            </Typography.Text>
          </Card>
        ) : (
          <Spin spinning={loading}>
            <Row gutter={[12, 12]}>
              <Col flex="1 1 240px">
                <Card>
                  <Statistic
                    title="Assessment completion"
                    value={status?.completed ?? 0}
                    suffix={`/ ${status?.total ?? 0}`}
                  />
                </Card>
              </Col>
              <Col flex="1 1 240px">
                <Card>
                  <Statistic
                    title="Total results"
                    value={risks?.totalResults ?? 0}
                  />
                </Card>
              </Col>
              <Col flex="1 1 240px">
                <Card>
                  <Statistic
                    title="Respondents"
                    value={risks?.respondentCount ?? 0}
                  />
                </Card>
              </Col>
              <Col flex="1 1 240px">
                <Card>
                  <Statistic
                    title="High-risk results"
                    value={
                      risks?.bandDistribution?.find((b) => b.ordinal === 0)
                        ?.count ?? 0
                    }
                    valueStyle={{ color: "#f06364" }}
                  />
                </Card>
              </Col>
              <Col flex="1 1 240px">
                <Card>
                  <Statistic
                    title="Open incidents"
                    value={incidents?.openIncidents ?? 0}
                  />
                </Card>
              </Col>
              <Col flex="1 1 240px">
                <Card>
                  <Statistic
                    title="Resolved in period"
                    value={incidents?.resolvedInPeriod ?? 0}
                  />
                </Card>
              </Col>
              <Col flex="1 1 240px">
                <Card>
                  <Statistic
                    title="Trainings in period"
                    value={trainingTotals.total}
                  />
                </Card>
              </Col>
              <Col flex="1 1 240px">
                <Card>
                  <Space direction="vertical">
                    <Statistic
                      title="Trainings completed"
                      value={trainingTotals.completed}
                    />
                    <Space size={4}>
                      <Tag color="red">{trainingTotals.cancelled} cancelled</Tag>
                      <Tag color="blue">
                        {trainingTotals.withFeedback} with feedback
                      </Tag>
                    </Space>
                  </Space>
                </Card>
              </Col>
            </Row>
          </Spin>
        )}
      </Col>
    </Col>
  );
}

export default CompanyReportsPage;
